import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../../data/content";
import { EASE, FadeIn, MaskedLine, Eyebrow } from "./Reveal";

const FILTERS = [
  { label: "All", value: "ALL", testid: "portfolio-filter-all" },
  { label: "Brand Launches", value: "BRAND LAUNCHES", testid: "portfolio-filter-brand-launches" },
  { label: "Corporate", value: "CORPORATE", testid: "portfolio-filter-corporate" },
  { label: "Private", value: "PRIVATE", testid: "portfolio-filter-private" },
];

export const Portfolio = () => {
  const [filter, setFilter] = useState("ALL");
  const navigate = useNavigate();
  const shown = PROJECTS.filter((p) => filter === "ALL" || p.type === filter);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="bg-sand py-32 sm:py-40"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-10">
          <div>
            <FadeIn>
              <Eyebrow>Portfolio</Eyebrow>
            </FadeIn>
            <h2 className="mt-8 font-serif font-light leading-[1.05] text-ink text-[clamp(2.4rem,5.5vw,4.8rem)]">
              <MaskedLine delay={0.1}>
                Selected <span className="italic text-olive">works.</span>
              </MaskedLine>
            </h2>
          </div>
          <FadeIn delay={0.2}>
            <div className="flex flex-wrap gap-3">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  data-testid={f.testid}
                  onClick={() => setFilter(f.value)}
                  className={`rounded-full border px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] transition-all duration-500 ${
                    filter === f.value
                      ? "border-ink bg-ink text-ivory"
                      : "border-ink/25 text-ink hover:border-ink"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </FadeIn>
        </div>

        <motion.div layout className="mt-20 columns-1 gap-7 sm:columns-2 lg:columns-3">
          <AnimatePresence mode="popLayout">
            {shown.map((p, i) => (
              <motion.figure
                layout
                key={p.name}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.8, ease: EASE }}
                data-testid={`portfolio-item-${i}`}
                onClick={() => navigate(`/work/${p.slug}`)}
                data-hover
                role="link"
                aria-label={`View the story of ${p.name}`}
                className="group relative mb-7 cursor-pointer break-inside-avoid overflow-hidden"
              >
                <div className={`overflow-hidden ${p.ratio}`}>
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  />
                </div>
                <figcaption className="pointer-events-none absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-[#2e2f1e]/75 via-[#2e2f1e]/10 to-transparent p-7 opacity-0 transition-opacity duration-700 group-hover:opacity-100">
                  <p className="translate-y-3 font-serif text-2xl font-light text-ivory transition-transform duration-700 group-hover:translate-y-0">
                    {p.name}
                  </p>
                  <p className="mt-2 translate-y-3 text-[10px] font-medium uppercase tracking-[0.3em] text-ivory/80 transition-transform delay-75 duration-700 group-hover:translate-y-0">
                    {p.location} — {p.typeLabel}
                  </p>
                  <p className="mt-4 flex translate-y-3 items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-pastel transition-transform delay-100 duration-700 group-hover:translate-y-0">
                    View the story <ArrowUpRight size={12} strokeWidth={1.75} />
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
